<script setup lang="ts">
import { computed, defineProps } from 'vue';
import { usePlanetStore } from '@/stores/planetStore';
import { useGameStore } from '@/stores/gameStore';
import { useAssetStore } from '@/stores/assetStore';
import Backend from '@/models/backend';
import StandardCard from '@/components/Elements/Cards/StandardCard.vue';
import GenericIcon from '@/components/Elements/Images/Icons/GenericIcon.vue';
import TechRequired from '@/components/Sections/Planets/PlanetTabs/Generic/TechRequired.vue';
import FerionDataTable from '@/components/Interface/DataTable/FerionDataTable.vue';
import HeaderTextFlex from '@/components/Interface/DataTable/Headers/HeaderTextFlex.vue';
import HeaderFixedWidthText from '@/components/Interface/DataTable/Headers/HeaderFixedWidthText.vue';
import CellFlexWidth from '@/components/Interface/DataTable/Cell/CellFlexWidth.vue';
import CellFixedWidth from '@/components/Interface/DataTable/Cell/CellFixedWidth.vue';
import PositionIcon from '@/components/Elements/Images/Icons/PositionIcon.vue';
import SimpleProgressBar from '@/components/Elements/ProgressBar/SimpleProgressBar.vue';
import ToggleSwitch from '@/components/Elements/ToggleSwitch/ToggleSwitch.vue';
import { logger } from '@/models/logger';
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

/**
 * Props
 */
const props = defineProps({
  planetId: {
    type: Number,
    required: true
  }
});

/**
 * Stores
 */
const planetStore = usePlanetStore();
const gameStore = useGameStore();
const assetStore = useAssetStore();

/**
 * Backend Service
 */
const backend = new Backend();


/**
 * Methods
 */
// Computed property to determine toggle state for each star
const getToggleState = (item: any) => {
  const planets = item.planetIds.map((id: number) => planetStore.findPlanetById(id)).filter(Boolean)
  const enabledCount = planets.filter((planet: any) => planet.ringworldConversionEnabled > 0).length
  
  if (enabledCount === 0) return false
  if (enabledCount === planets.length) return true
  return 'mixed' // Some enabled, some disabled
}

// Toggle handler function
const handleToggle = async (item: any) => {
  const planets = item.planetIds.map((id: number) => planetStore.findPlanetById(id)).filter(Boolean)
  const enabledCount = planets.filter((planet: any) => planet.ringworldConversionEnabled > 0).length
  
  // Determine which planets to toggle based on the requirements:
  // - If only some planets are enabled (mixed state), disable all enabled ones
  // - If all are disabled, enable all
  // - If all are enabled, disable all
  let planetsToToggle: number[]
  
  if (enabledCount === 0) {
    // All disabled - enable all
    planetsToToggle = item.planetIds
  } else if (enabledCount === planets.length) {
    // All enabled - disable all
    planetsToToggle = item.planetIds
  } else {
    // Mixed state - disable all enabled ones
    planetsToToggle = planets
      .filter((planet: any) => planet.ringworldConversionEnabled > 0)
      .map((planet: any) => planet.id)
  }
  
  try {
    await backend.toggleRingworldConversion(planetsToToggle)
  } catch (error) {
    logger.error('Failed to toggle ringworld conversion:', error)
  }
}

// Simple filter function that returns all stars as an array
const filters = (stars: any[]) => {
  return stars
}

/**
 * Computed
 */
const currentPlanet = computed(() => {
  return planetStore.findPlanetById(props.planetId);
});

const currentStarData = computed(() => {
  if (!currentPlanet.value) return new Map()
  
  // Get all stars data (returns array) and find current star
  const starsArray = planetStore.getStarsWithNonRingworldPlanets()
  const currentStarId = currentPlanet.value.starId
  
  // Find the star data by matching planet IDs (since we know our planet is in this star)
  const currentStar = starsArray.find(star => star.planetIds.includes(currentPlanet.value!.id))
  
  if (!currentStar) return new Map()
  
  // Convert to Map format like in the modal with additional fields
  const resultMap = new Map()
  const starWithSortableLocation = {
    ...currentStar,
    id: currentStarId, // Add id field required by FerionDataTable
    starId: currentStarId, // Add starId for compatibility
    locationX: currentStar.location.x,
    locationY: currentStar.location.y,
    coordinatesText: `(${currentStar.location.x}, ${currentStar.location.y})`
  }
  resultMap.set(currentStarId, starWithSortableLocation)
  return resultMap
});
const ringworldOreName = computed(() => {
  const oreId = gameStore.player?.ringworldOreId;
  if (!oreId) return 'ore';
  return assetStore.findOreById(oreId)?.oreName || 'ore';
});

const homeWorldPlanetTypeName = computed(() => {
  const planetTypeId = gameStore.player?.homeWorldPlanetType;
  if (!planetTypeId) return 'paradise';
  return assetStore.findPlanetTypeById(planetTypeId)?.name || 'paradise';
});

const hasRingworldTech = computed(() => {
  const tech = assetStore.findTechByCode('ringworlds');
  return tech && gameStore.hasPlayerTech(tech.id);
});

const ringworldTechId = computed(() => {
  const tech = assetStore.findTechByCode('ringworlds');
  return tech?.id || 0;
});



</script>

<template>
  <StandardCard :title="t('planets.ringworld.title')" topPadding="2">
    <span v-html="t('planets.ringworld.description', {
      planetCount: `<span class='text-slate-300'>5</span>`,
      planetType: `<span class='text-slate-300'>${homeWorldPlanetTypeName}</span>`,
      oreAmount: gameStore.ringworldOrePerPlanet || 5,
      oreName: ringworldOreName
    })"></span>
    <GenericIcon v-if="gameStore.player?.ringworldOreId" :type="'ore' + gameStore.player.ringworldOreId" class="inline-block mx-1" />
</StandardCard>

  <StandardCard topPadding="2" v-if="!hasRingworldTech">
  <TechRequired
    :techId="ringworldTechId"
    :imageCode="'ringworlds'"
    :text="t('planets.ringworld.needTech')"
  />
  </StandardCard>

  <!-- Star System Conversion Table -->
  <div v-else class="mt-2">
    <FerionDataTable
      :data="currentStarData"
      :filters="filters"
      tableStoreName="ringworldConversionStars"
      :selector="false"
      :footerBar="false"
      :heightLimit="'h-auto'"
      :click="() => {}"
      :doubleClick="() => {}"
      class="mb-2"
    >
      
      <template #columns="{ sort, sortColumn, sortDirection }">
        <HeaderTextFlex
          min-width="16"
          column="name"
          :sortColumn="sortColumn"
          :sortDirection="sortDirection"
          @sort="sort"
          class="pl-2 rounded-tl"
        >
          {{ t('planets.ringworld.columns.starSystem') }}
        </HeaderTextFlex>
        <HeaderFixedWidthText
          width="4"
          column="locationX"
          :sortColumn="sortColumn"
          :sortDirection="sortDirection"
          @sort="sort"
          align="center"
        >
          {{ t('planets.ringworld.columns.position') }}
        </HeaderFixedWidthText>
        <HeaderFixedWidthText
          width="10"
          column="nonRingworldCount"
          :sortColumn="sortColumn"
          :sortDirection="sortDirection"
          @sort="sort"
          align="center"
        >
          {{ t('planets.ringworld.columns.planetsOwned') }}
        </HeaderFixedWidthText>
        <HeaderFixedWidthText
          width="10"
          column="homeWorldTypeCount"
          :sortColumn="sortColumn"
          :sortDirection="sortDirection"
          @sort="sort"
          align="center"
        >
          {{ t('planets.ringworld.columns.paradisePlanets', { planetType: homeWorldPlanetTypeName }) }}
        </HeaderFixedWidthText>
        <HeaderFixedWidthText
          width="12"
          column="planetsConverting"
          :sortColumn="sortColumn"
          :sortDirection="sortDirection"
          @sort="sort"
          align="center"
        >
          {{ t('planets.ringworld.columns.planetsConverting') }}
        </HeaderFixedWidthText>
        <HeaderFixedWidthText
          width="20"
          column="totalConversionProgress"
          :sortColumn="sortColumn"
          :sortDirection="sortDirection"
          @sort="sort"
          align="left"
        >
          {{ t('planets.ringworld.columns.conversionProgress') }}
        </HeaderFixedWidthText>
        <HeaderFixedWidthText
          class="rounded-tr pr-2"
          width="10"
          column="planetsConverting"
          :sortColumn="sortColumn"
          :sortDirection="sortDirection"
          @sort="sort"
          align="center"
        >
          {{ t('planets.ringworld.columns.toggleAll') }}
        </HeaderFixedWidthText>
      </template>
      
      <template #rows="{ item }">
        <CellFlexWidth min-width="16" class="pl-2">
          <div class="text-slate-200 font-medium">{{ item.name }}</div>
        </CellFlexWidth>

        <CellFixedWidth width="4" align="center">
          <PositionIcon :x="item.location.x" :y="item.location.y" />
        </CellFixedWidth>

        <CellFixedWidth width="10" align="center">
          <div :class="item.nonRingworldCount === 5 ? 'text-green-400' : 'text-red-400'">
            {{ item.nonRingworldCount }} / 5
          </div>
        </CellFixedWidth>
        
        <CellFixedWidth width="10" align="center">
          <div :class="item.homeWorldTypeCount === 5 ? 'text-green-400' : item.homeWorldTypeCount > 0 ? 'text-yellow-400' : 'text-red-400'">
            {{ item.homeWorldTypeCount }} / 5
          </div>
        </CellFixedWidth>
        
        <CellFixedWidth width="12" align="center">
          <div :class="item.planetsConverting === 5 ? 'text-green-400' : 'text-red-400'">
            {{ item.planetsConverting }} / 5
          </div>
        </CellFixedWidth>
        
        <CellFixedWidth width="20" align="left">
          <SimpleProgressBar 
            :percentage="(item.totalConversionProgress / item.maxConversionProgress) * 100"
            :text="`${Math.round((item.totalConversionProgress / item.maxConversionProgress) * 100)}%`"
            :subtext="`${item.totalConversionProgress} / ${item.maxConversionProgress}`"
            color="blue"
            :height="20"
          />
        </CellFixedWidth>
        
        <CellFixedWidth width="10" align="center" class="pr-2 mb-2">
          <ToggleSwitch 
            :modelValue="getToggleState(item) === true"
            @update:modelValue="handleToggle(item)"
          />
        </CellFixedWidth>
      </template>
    </FerionDataTable>
  </div>

  
</template>

<style scoped lang="scss">
// Component specific styles if needed
</style> 